import { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { useBranding } from '@/hooks/useBranding';
import { buildBreadcrumbs } from '@/lib/route-registry';
import { Skeleton } from '@/components/ui/Skeleton';

/* Fallback affiché dans la zone de contenu pendant le chargement d'un chunk
   de page (code splitting) : silhouette titre + cartes, sidebar intacte. */
function PageSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-4 w-96" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Raccourci global ⌘K / Ctrl+K.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
  const { data: branding } = useBranding();
  const location = useLocation();
  const crumbs = buildBreadcrumbs(location.pathname);
  const mobileTitle = crumbs.length > 0 ? crumbs[crumbs.length - 1].labelKey : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Suit le breakpoint lg : `inert` ne doit neutraliser le tiroir fermé
  // qu'en mobile — en desktop la sidebar est toujours visible et active.
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Referme le menu mobile à chaque navigation.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const themeStyle = useMemo(() => {
    if (!branding?.accentColor || branding.accentColor === '#4F46E5') return undefined;
    return {
      '--accent': branding.accentColor,
      // Le texte accentué suit la couleur de marque ; en sombre, la feuille de
      // style reprend la main (règle !important sur [data-brand-root]) pour
      // garder une teinte lisible sur surface sombre.
      '--accent-text': branding.accentColor,
      '--accent-hover': branding.accentHoverColor || branding.accentColor,
      '--accent-soft': branding.accentSoftColor || `${branding.accentColor}14`,
    } as React.CSSProperties;
  }, [branding?.accentColor, branding?.accentHoverColor, branding?.accentSoftColor]);

  return (
    <div data-brand-root className="flex h-screen overflow-hidden bg-bg" style={themeStyle}>
      {/* Skip link : premier élément focusable, visible uniquement au focus —
          évite ~20 tabulations de sidebar avant d'atteindre le contenu. */}
      <a
        href="#contenu-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-text focus:shadow-elevated"
      >
        {t('nav.skipToContent')}
      </a>
      {/* Sidebar : fixe sur desktop, tiroir coulissant sur mobile. `inert`
          quand le tiroir mobile est fermé : hors écran ≠ hors tabulation. */}
      <div
        inert={!isDesktop && !sidebarOpen ? true : undefined}
        className={cn(
          'fixed inset-y-0 left-0 z-40 transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Sidebar />
      </div>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Topbar desktop : fil d'Ariane, palette ⌘K, contexte tenant/env. */}
        <Topbar onOpenPalette={() => setPaletteOpen(true)} />

        {/* Barre mobile avec bouton menu */}
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-surface px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('nav.openMenu')}
            className="rounded p-1.5 text-muted hover:bg-surface-subtle hover:text-text"
          >
            <Menu className="h-5 w-5" />
          </button>
          {/* Sur mobile, le fil d'Ariane du Topbar n'existe pas : le titre de la
              page courante dit où l'on est (la marque est dans le tiroir). */}
          <span className="truncate text-[13px] font-semibold text-text">
            {mobileTitle ? t(mobileTitle) : branding?.brandName || t('brand.pricing')}
          </span>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label={t('palette.openAria')}
            className="ml-auto rounded p-1.5 text-muted hover:bg-surface-subtle hover:text-text"
          >
            <Search className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <main id="contenu-principal" tabIndex={-1} className="flex-1 overflow-y-auto scrollbar-thin outline-none">
          <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 lg:px-8 lg:py-6">
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
